/**
 * Leave Request View Page — full detail with role-gated approve/reject actions.
 *
 * Employees see their own request status.
 * Managers/admins see approve + reject buttons when status=pending.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import {
  Button,
  Card,
  CardContent,
  Badge,
  StatusBadge,
} from '@yesbos/ui';
import { ArrowLeft, CheckCircle2, XCircle, Ban } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Types ────────────────────────────────────────────────────────────────────

interface LeaveRequest {
  id: string;
  employee_id?: string;
  leave_type_id?: string;
  leave_type_name?: string;
  start_date: string;
  end_date: string;
  reason?: string;
  status: string;
  rejection_reason?: string;
  approval_date?: string;
  approved_by?: string;
  employee_name?: string;
  created_at?: string;
}

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string, method = 'POST', body?: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

// ── Status color map ──────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

// ── Detail Row ────────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function LeaveViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useOne<LeaveRequest>({
    resource: 'leave_requests',
    id: id!,
  });
  const req = data?.data as LeaveRequest | undefined;

  const [loading, setLoading] = React.useState(false);
  const [rejectMode, setRejectMode] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const [toast, setToast] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleApprove() {
    setLoading(true);
    try {
      const res = await callBLL(`/v1/hros/leave/requests/${id}/approve`);
      if (res.success) {
        setToast({ message: 'Leave request approved.', type: 'success' });
        refetch();
      } else {
        setToast({ message: res.error?.message ?? 'Failed to approve.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleReject() {
    if (!rejectReason.trim()) return;
    setLoading(true);
    try {
      const res = await callBLL(`/v1/hros/leave/requests/${id}/reject`, 'POST', {
        reason: rejectReason.trim(),
      });
      if (res.success) {
        setToast({ message: 'Leave request rejected.', type: 'success' });
        setRejectMode(false);
        setRejectReason('');
        refetch();
      } else {
        setToast({ message: res.error?.message ?? 'Failed to reject.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    setLoading(true);
    try {
      const res = await callBLL(`/v1/hros/leave/requests/${id}/cancel`);
      if (res.success) {
        setToast({ message: 'Leave request cancelled.', type: 'success' });
        refetch();
      } else {
        setToast({ message: res.error?.message ?? 'Failed to cancel.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6 max-w-2xl">
        <div className="h-8 w-48 rounded-md bg-muted animate-pulse" />
        <div className="h-48 rounded-lg border bg-muted animate-pulse" />
      </div>
    );
  }

  if (!req) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Leave request not found.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/leave')}>
          Back to Leave
        </Button>
      </div>
    );
  }

  const dayCount = (() => {
    try {
      const ms = new Date(req.end_date).getTime() - new Date(req.start_date).getTime();
      return Math.max(1, Math.round(ms / 86_400_000) + 1);
    } catch { return null; }
  })();

  const isPending = req.status === 'pending';

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leave')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Leave Request</h1>
          {req.employee_name && (
            <p className="text-sm text-muted-foreground">{req.employee_name}</p>
          )}
        </div>
        <StatusBadge status={req.status} />
      </div>

      {/* Details */}
      <Card>
        <CardContent className="pt-5 grid gap-4 sm:grid-cols-2">
          {req.leave_type_name && (
            <DetailRow label="Leave Type" value={
              <Badge variant="outline">{req.leave_type_name}</Badge>
            } />
          )}
          <DetailRow
            label="Duration"
            value={
              <span>
                {new Date(req.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                {' → '}
                {new Date(req.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                {dayCount !== null && (
                  <span className="text-muted-foreground ml-1">({dayCount} day{dayCount !== 1 ? 's' : ''})</span>
                )}
              </span>
            }
          />
          {req.reason && <DetailRow label="Reason" value={req.reason} />}
          {req.rejection_reason && (
            <div className="sm:col-span-2">
              <DetailRow
                label="Rejection Reason"
                value={<span className="text-destructive">{req.rejection_reason}</span>}
              />
            </div>
          )}
          {req.approval_date && (
            <DetailRow
              label="Actioned On"
              value={new Date(req.approval_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            />
          )}
          {req.created_at && (
            <DetailRow
              label="Submitted On"
              value={new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            />
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      {isPending && (
        <div className="space-y-3">
          {!rejectMode ? (
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleApprove}
                disabled={loading}
                className="gap-1.5"
              >
                <CheckCircle2 className="h-4 w-4" />
                Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => setRejectMode(true)}
                disabled={loading}
                className="gap-1.5"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="gap-1.5"
              >
                <Ban className="h-4 w-4" />
                Cancel Request
              </Button>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="pt-4 pb-4 space-y-3">
                <p className="text-sm font-medium">Rejection Reason</p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejection…"
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || loading}
                  >
                    {loading ? 'Rejecting…' : 'Confirm Reject'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setRejectMode(false); setRejectReason(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default LeaveViewPage;
